import React, { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import InterviewCard from "./interview/InterviewCard";
import QuestionSubmissionCard from "./QuestionSubmissionCard";
import AMAFeedCard from "./ama/AMAFeedCard";
import { Interview } from "@/types/interview";
import { useInfiniteInterviews } from "@/hooks/useInfiniteInterviews";
import { Loader2 } from "lucide-react";

interface InterviewFeedProps {
  forceRefresh?: boolean;
}

const InterviewFeed: React.FC<InterviewFeedProps> = ({ forceRefresh = false }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  
  const { 
    data, 
    isLoading, 
    error, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage 
  } = useInfiniteInterviews();

  // Flatten all pages into a single array
  const allInterviews = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flat();
  }, [data]);

  // Sort interviews chronologically (newest first)
  const sortedInterviews = useMemo(() => {
    if (!allInterviews || allInterviews.length === 0) return [];
    
    return [...allInterviews].sort((a: Interview, b: Interview) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA;
    });
  }, [allInterviews]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (isLoading) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.5 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isLoading]);

  useEffect(() => {
    if (forceRefresh) {
      queryClient.invalidateQueries({ queryKey: ['publishedInterviews', 'infinite'] });
    }
  }, [forceRefresh, queryClient]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="space-y-12">
          {[1, 2, 3].map((index) => (
            <div key={index} className="animate-pulse">
              <div className="h-64 bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {t('feed.errorLoading')} {error instanceof Error ? error.message : t('common.error')}
          <br />
          {t('feed.refreshPage')}
        </div>
      </div>
    );
  }

  if (!isLoading && sortedInterviews.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
          {t('feed.noInterviews')}
        </div>
        <QuestionSubmissionCard />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="space-y-12">
        <AMAFeedCard />
        {sortedInterviews.map((interview: Interview, index: number) => {
          return (
            <InterviewCard 
              key={interview.id}
              id={interview.id}
              context={interview.context}
              contextUrl={interview.contextUrl}
              question={interview.question}
              answer={interview.answer}
              intervieweeName={interview.intervieweeName}
              title={interview.title}
              jobTitle={interview.jobTitle}
              company={interview.company}
              timestamp={interview.timestamp}
              photoUrl={interview.photoUrl}
              photoUrl2={interview.photoUrl2}
              tags={interview.tags}
              aiSummary={interview.aiSummary}
              aiResponse={interview.aiResponse}
              isLiked={interview.isLiked}
              isBookmarked={interview.isBookmarked}
              isReversed={index % 2 !== 0}
              articleNumber={index + 1} 
              summary_status={interview.summary_status}
              summary_error={interview.summary_error}
              sourceArticles={interview.sourceArticles}
              photographerCredit={interview.photographerCredit}
              articleType={interview.articleType}
              isSponsored={interview.isSponsored}
              sponsorCompany={interview.sponsorCompany}
              sponsorLogoUrl={interview.sponsorLogoUrl}
              sponsorLogoPosition={interview.sponsorLogoPosition}
            />
          );
        })}
        
        {/* Infinite scroll trigger */}
        <div ref={loadMoreRef} className="py-8 flex justify-center">
          {isFetchingNextPage && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Laster flere artikler...</span>
            </div>
          )}
          {!hasNextPage && sortedInterviews.length > 0 && (
            <p className="text-muted-foreground text-sm">Du har nådd slutten av feeden</p>
          )}
        </div>
        
        <QuestionSubmissionCard />
      </div>
    </div>
  );
};

export default InterviewFeed;
